import { cn } from '@/lib/utils'
import { Flower2, Hotel, MonitorCheck, PartyPopper, PersonStanding, Users } from 'lucide-react'
import React from 'react'

interface Event {
  id: string
  title: string
  startTime: string
  endTime: string
  forRelatives: boolean
  forFriends: boolean
  color: string
  icon?: React.ReactNode
}

const events: Event[] = [
  {
    id: 'relatives-gather',
    title: '親族集合',
    startTime: '11:00',
    endTime: '12:00',
    forRelatives: true,
    forFriends: false,
    color: 'bg-gradient-orange-to-pink',
    icon: <Users className="w-4 h-4" />
  },
  {
    id: 'friends-gather',
    title: '友人集合',
    startTime: '12:00',
    endTime: '12:45',
    forRelatives: false,
    forFriends: true,
    color: 'bg-gradient-orange-to-green',
    icon: (<PersonStanding className="w-4 h-4" /> )
  },
  {
    id: 'ceremony',
    title: '挙式',
    startTime: '12:30',
    endTime: '12:45',
    forRelatives: true,
    forFriends: false,
    color: 'bg-gradient-orange-to-pink',
    icon: <Hotel className="w-4 h-4" />
  },
  {
    id: 'flower-shower',
    title: 'フラワーシャワー',
    startTime: '12:45',
    endTime: '13:00',
    forRelatives: true,
    forFriends: true,
    color: 'bg-gradient-orange-to-purple',
    icon: <Flower2 className="w-4 h-4" />
  },
  {
    id: 'reception',
    title: '受付',
    startTime: '13:00',
    endTime: '13:30',
    forRelatives: true,
    forFriends: true,
    color: 'bg-gradient-orange-to-purple',
    icon: <MonitorCheck className="w-4 h-4" />
  },
  {
    id: 'main-reception',
    title: '披露宴',
    startTime: '13:30',
    endTime: '16:00',
    forRelatives: true,
    forFriends: true,
    color: 'bg-gradient-orange-to-purple',
    icon: <PartyPopper className="w-4 h-4" />
  }
]

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

const calculateHeight = (startTime: string, endTime: string): number => {
  const duration = timeToMinutes(endTime) - timeToMinutes(startTime)
  return duration * 2
}

const calculateTop = (startTime: string, baseTime: string): number => {
  const offset = timeToMinutes(startTime) - timeToMinutes(baseTime)
  return offset * 2
}

const generateTimeMarks = () => {
  const marks = []
  for (let hour = 11; hour <= 16; hour++) {
    marks.push(`${hour}:00`)
    if (hour === 12) {
      marks.push('12:30')
      marks.push('12:45')
    } else if (hour < 16) {
      marks.push(`${hour}:30`)
    }
  }
  return marks
}

export default function TimeTable() {
  return (
    <div className="bg-white/50 backdrop-blur-md rounded-3xl p-4 sm:p-6 md:p-8 shadow-lg">
      <h2 className="text-3xl md:text-4xl font-dancing text-center mb-6 md:mb-8 text-gray-800">
        Wedding Time Table
      </h2>

      <div className="relative">
        <div className="grid grid-cols-[60px_1fr_1fr] sm:grid-cols-[80px_1fr_1fr] gap-1 sm:gap-2 mb-4">
          <div className="text-center py-2 font-bold text-xs sm:text-sm text-gray-700 bg-white/50 rounded-lg">
            時間
          </div>
          <div className="text-center py-2 font-bold text-xs sm:text-sm text-gray-700 bg-gradient-orange-to-pink rounded-lg flex items-center justify-center gap-1">
            <Users className="w-4 h-4" />
            親族
          </div>
          <div className="text-center py-2 font-bold text-xs sm:text-sm text-gray-700 bg-gradient-orange-to-green rounded-lg flex items-center justify-center gap-1">
          <PersonStanding className="w-4 h-4" />
            友人
          </div>
        </div>

        <div
          className="relative"
          style={{ height: `${(16 - 11) * 60 * 2}px` }}
        >
          <div className="grid grid-cols-[60px_1fr] sm:grid-cols-[80px_1fr] gap-1 sm:gap-2 h-full">
            <div className="relative bg-white/50 border-t-1 rounded-t-lg">
              {generateTimeMarks().map((time) => (
                <div
                  key={time}
                  className="absolute left-0 right-0 flex items-center justify-center text-xs sm:text-sm font-semibold text-gray-600"
                  style={{
                    top: `${calculateTop(time, '11:00')}px`,
                    height: '20px'
                  }}
                >
                  <span className={cn(`px-1 ${time === '16:00' ? 'bg-white/50 border-b-1 rounded-b-lg py-[0.20rem] px-[0.80rem]' : ''}`)}>{time}</span>
                </div>
              ))}
              {generateTimeMarks().map((time, index) => (
                  <div
                    key={time}
                    className={cn(`absolute left-0 right-0 border-t border-gray-300 ${index === 0 ? 'border-t-0' : ''}`)}
                    style={{ top: `${calculateTop(time, '11:00')}px` }}
                  />
              ))}
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-1 sm:gap-2 h-full">
                <div className="relative bg-gradient-to-b from-orange-100/30 to-pink-100/30 rounded-lg">
                  {events.filter(event => event.forRelatives && !event.forFriends).map((event) => (
                    <div
                      key={event.id}
                      className="absolute left-1 right-1 px-1"
                      style={{
                        top: `${calculateTop(event.startTime, '11:00')}px`,
                        height: `${calculateHeight(event.startTime, event.endTime)}px`
                      }}
                    >
                      <div
                        className={`h-full rounded-xl shadow-sm flex ${event.id === 'ceremony' ? 'flex-row' : 'flex-col'} items-center justify-center gap-1 p-2 ${event.color}`}
                        style={{ height: 'calc(100% - 4px)', marginTop: '2px', marginBottom: '2px' }}
                      >
                        <div className="animate-wiggle">
                          {event.icon}
                        </div>
                        <div className="text-center">
                          <div className="text-xs sm:text-sm font-bold text-gray-800">
                            {event.startTime}~ {event.title}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="relative bg-gradient-to-b from-orange-100/30 to-green-100/30 rounded-lg">
                  {events.filter(event => event.forFriends && !event.forRelatives).map((event) => (
                    <div
                      key={event.id}
                      className="absolute left-1 right-1 px-1"
                      style={{
                        top: `${calculateTop(event.startTime, '11:00')}px`,
                        height: `${calculateHeight(event.startTime, event.endTime)}px`
                      }}
                    >
                      <div
                        className={`h-full rounded-xl shadow-sm flex ${event.id === 'ceremony' ? 'flex-row' : 'flex-col'} items-center justify-center gap-1 p-2 ${event.color}`}
                        style={{ height: 'calc(100% - 4px)', marginTop: '2px', marginBottom: '2px' }}
                      >
                        <div className="animate-wiggle">
                          {event.icon}
                        </div>
                        <div className="text-center">
                          <div className="text-xs sm:text-sm font-bold text-gray-800">
                            {event.startTime}~ {event.title}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {events.filter(event => event.forRelatives && event.forFriends).map((event) => (
                <div
                  key={event.id}
                  className="absolute left-0 right-0 px-1"
                  style={{
                    top: `${calculateTop(event.startTime, '11:00')}px`,
                    height: `${calculateHeight(event.startTime, event.endTime)}px`
                  }}
                >
                  <div
                    className={`rounded-xl shadow-sm flex ${event.id === 'flower-shower' ? 'flex-row' : 'flex-col'} items-center justify-center gap-1 p-2 mx-1 sm:mx-2 ${event.color}`}
                    style={{ height: 'calc(100% - 4px)', marginTop: '2px', marginBottom: '2px' }}
                  >
                    <div className="animate-wiggle">
                      {event.icon}
                    </div>
                    <div className="text-center">
                      <div className="text-xs sm:text-sm font-bold text-gray-800">
                        {event.startTime}~ {event.title}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 md:mt-8 text-center text-xs sm:text-sm text-gray-600">
        <p>※ 友人のみなさまはフラワーシャワー（12:45）からご参加ください</p>
      </div>
    </div>
  )
}
